import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useSafeguardAdminSignalRContext } from '@/context_providers/signal_r_context/SafeguardAdminSignalRListener';
import { usePvdConfigOperations } from '@/hooks/pvd_config_hooks/usePvdConfigOperations';
import { useEmergencyConfigOperations } from '@/hooks/emergency_config_hooks/useEmergencyConfigOperations';
import { EditPvdConfigModal } from '@/views/UserViews/compass_configurations/pvd/modals/EditPvdConfigModal';
import { EditEmergencyConfigModal } from '@/views/UserViews/compass_configurations/emergency/modals/EditEmergencyConfigModal';
import { getAlertOptionStringKey } from '@/utils/unified_config_helper/unifiedConfigDisplayHelpers';
import { useTranslation } from 'react-i18next';
import Loading from '@/components/loading/Loading';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import COLORS from '@/resources/style/Themes';
import type {
  PvdConfigurationOnAzure,
  EmergencyConfigurationOnAzure,
  UpdatePvdConfigRequest,
  UpdateEmergencyConfigRequest,
} from '@/../generated-client/models';

const summarizeVoltage = (config: PvdConfigurationOnAzure): string => {
  const anyChip = config.voltage_allowed_settings.some(Boolean);
  if (!anyChip && !config.adaptive_voltage_allowed) return 'Disabled';
  if (config.voltage_allowed_settings.every(Boolean) && config.adaptive_voltage_allowed) return 'Enabled';
  return 'Custom';
};

const summarizeCurrent = (config: PvdConfigurationOnAzure): string => {
  const anyChip = config.current_allowed_settings.some(Boolean);
  if (!anyChip && !config.adaptive_current_allowed) return 'Disabled';
  if (config.current_allowed_settings.every(Boolean) && config.adaptive_current_allowed) return 'Enabled';
  return 'Custom';
};

const ConfigSummaryField = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const EditBaseConfigurationTab: React.FC = () => {
  const { t } = useTranslation();
  const { organization } = useSafeguardAdminSignalRContext();
  const [pvdBaseConfig, setPvdBaseConfig] = useState<PvdConfigurationOnAzure | null>(null);
  const [emergencyBaseConfig, setEmergencyBaseConfig] = useState<EmergencyConfigurationOnAzure | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditPvdModal, setShowEditPvdModal] = useState(false);
  const [showEditEmergencyModal, setShowEditEmergencyModal] = useState(false);

  const { fetchConfigs: fetchPvdConfigs, updateConfig: updatePvdConfig } = usePvdConfigOperations({
    updateLocalState: (op, config) => {
      if (op === 'update') setPvdBaseConfig(config);
    },
  });

  const { fetchConfigs: fetchEmergencyConfigs, updateConfig: updateEmergencyConfig } = useEmergencyConfigOperations({
    updateLocalState: (op, config) => {
      if (op === 'update') setEmergencyBaseConfig(config);
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!organization?.org_code) return;
      setLoading(true);
      const [pvdConfigs, emergencyConfigs] = await Promise.all([
        fetchPvdConfigs(organization.org_code),
        fetchEmergencyConfigs(organization.org_code),
      ]);
      if (cancelled) return;
      setPvdBaseConfig(pvdConfigs?.find((c) => c.is_base_config) ?? null);
      setEmergencyBaseConfig(emergencyConfigs?.find((c) => c.is_base_config) ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.org_code]);

  const handleUpdatePvd = async (req: UpdatePvdConfigRequest) => {
    const result = await updatePvdConfig(req);
    if (result) setShowEditPvdModal(false);
  };

  const handleUpdateEmergency = async (req: UpdateEmergencyConfigRequest) => {
    const result = await updateEmergencyConfig(req);
    if (result) setShowEditEmergencyModal(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: COLORS.SAFEGUARD_BLUE }}>
        Edit Base Configuration
      </Typography>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>Base PVD Configuration</Typography>
          <IconButton
            onClick={() => setShowEditPvdModal(true)}
            disabled={!pvdBaseConfig}
            sx={{ color: 'white' }}
          >
            <Edit />
          </IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          {pvdBaseConfig ? (
            <Box sx={{ display: 'flex', gap: 4 }}>
              <ConfigSummaryField label="Voltage Sensitivity" value={summarizeVoltage(pvdBaseConfig)} />
              <ConfigSummaryField label="Current Sensitivity" value={summarizeCurrent(pvdBaseConfig)} />
            </Box>
          ) : (
            <Typography color="text.secondary">No Base PVD configuration was found for this organization.</Typography>
          )}
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>Base Emergency Configuration</Typography>
          <IconButton
            onClick={() => setShowEditEmergencyModal(true)}
            disabled={!emergencyBaseConfig}
            sx={{ color: 'white' }}
          >
            <Edit />
          </IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          {emergencyBaseConfig ? (
            <Box sx={{ display: 'flex', gap: 4 }}>
              <ConfigSummaryField
                label="Impact Alerts"
                value={t(getAlertOptionStringKey(emergencyBaseConfig.impact_detection))}
              />
              <ConfigSummaryField
                label="Fall Detection"
                value={t(getAlertOptionStringKey(emergencyBaseConfig.fall_detection))}
              />
              <ConfigSummaryField
                label="Arc Flash"
                value={t(getAlertOptionStringKey(emergencyBaseConfig.arc_flash_detection))}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">
              No Base Emergency configuration was found for this organization.
            </Typography>
          )}
        </Box>
      </Paper>

      {showEditPvdModal && pvdBaseConfig && (
        <EditPvdConfigModal
          show={showEditPvdModal}
          onHide={() => setShowEditPvdModal(false)}
          config={pvdBaseConfig}
          onUpdate={handleUpdatePvd}
        />
      )}

      {showEditEmergencyModal && emergencyBaseConfig && (
        <EditEmergencyConfigModal
          show={showEditEmergencyModal}
          onHide={() => setShowEditEmergencyModal(false)}
          config={emergencyBaseConfig}
          onUpdate={handleUpdateEmergency}
        />
      )}
    </Box>
  );
};

export default withAuthenticationRequired(EditBaseConfigurationTab, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
